import {
    createDefaultExtrudeDirectionDefinition,
    createDefaultExtrudeThinFeatureDefinition,
} from '../../../utils/feature/normalizeExtrudeFeatureDefinition';
import { CutExtrudeDefinitionCoordinator } from './CutExtrudeDefinitionCoordinator';
import type { ExtrudeFeatureEditDefinition } from './ExtrudeFeatureEditDefinition';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/extrude 层；
 * 2. 负责构建拉伸编辑定义的领域默认值；
 * 3. 该类只处理默认值生成，不承担净化、命令映射和特征定义构建职责。
 *
 * 文件职责：
 * 1. 统一生成 Direction / ThinFeature 的默认定义；
 * 2. 统一生成 boss / cut 两类编辑定义的默认快照；
 * 3. 为 facade 与宿主属性栏提供稳定的默认值入口。
 */
export class ExtrudeFeatureEditDefinitionDefaultBuilder {
    /**
     * 创建默认方向定义。
     *
     * @param overrides 覆盖项。
     * @returns 默认方向定义。
     */
    public static createDefaultDirection(
        overrides: Partial<ExtrudeDirectionDefinition> = {},
    ): ExtrudeDirectionDefinition {
        return createDefaultExtrudeDirectionDefinition(overrides);
    }

    /**
     * 创建默认薄壁定义。
     *
     * @returns 默认薄壁定义。
     */
    public static createDefaultThinFeature(): ExtrudeThinFeatureDefinition {
        return createDefaultExtrudeThinFeatureDefinition();
    }

    /**
     * 创建默认编辑定义。
     *
     * @param input 默认值构造输入。
     * @returns 默认编辑定义。
     */
    public static createDefault(input: {
        featureKind?: ExtrudeFeatureKind;
        operation?: ExtrudeOperation;
        cutOptions?: ExtrudeCutDefinition | null;
    } = {}): ExtrudeFeatureEditDefinition {
        const featureKind = input.featureKind ?? 'boss-extrude';
        const operation = input.operation ?? 'boss';
        const isCutExtrude = featureKind === 'cut-extrude' || operation === 'cut';
        const defaultDirection1 = this.createDefaultDirection();
        const defaultDirection2 = this.createDefaultDirection({
            enabled: false,
            reverse: false,
        });
        const defaultDefinition: ExtrudeFeatureEditDefinition = {
            featureKind,
            operation,
            direction1: defaultDirection1,
            direction2: defaultDirection2,
            thinFeature: this.createDefaultThinFeature(),
            mergeResult: !isCutExtrude,
            selectedContoursInputActive: false,
            cutOptions: isCutExtrude
                ? CutExtrudeDefinitionCoordinator.createDefaultCutOptions({
                    direction1: defaultDirection1,
                    direction2: defaultDirection2,
                    existing: input.cutOptions,
                })
                : null,
        };

        return isCutExtrude
            ? CutExtrudeDefinitionCoordinator.synchronizeDefinition(defaultDefinition)
            : defaultDefinition;
    }
}
